import Cookie from 'js-cookie'
import { getCookieDomain, isSecureContext } from '../utils/cookie-domain'
import { MONTILLA_AD_COOKIE_NAME } from '../constants'
import { parseAdLinkId, adAttributionAttrs, type AdLinkParts } from '../utils/ad-link'

/**
 * MontillaAds first-touch attribution store.
 *
 * The FIRST valid `mad` the SDK ever sees for a visitor is persisted to a
 * root-domain cookie and thereafter reported on every load/page — so a
 * conversion that happens several pages (or days) after the ad click is still
 * attributed to that click. Subsequent `mad`s do NOT overwrite the first touch.
 *
 * Kept intentionally small: only the raw id + a first-seen timestamp are
 * stored; the decoded parts are re-derived on read.
 */

/** Persisted shape of the first-touch cookie. */
interface PersistedAdAttribution {
  /** Raw `mad` token (the opaque ad link id). */
  id: string
  /** ISO timestamp of the first time this ad was seen. */
  ts: string
}

const COOKIE_MAX_AGE_DAYS = 90

function hasWindow(): boolean {
  return typeof window !== 'undefined' && typeof document !== 'undefined'
}

function read(): PersistedAdAttribution | null {
  if (!hasWindow()) return null
  const raw = Cookie.get(MONTILLA_AD_COOKIE_NAME)
  if (!raw) return null
  try {
    const parsed = JSON.parse(raw) as PersistedAdAttribution
    return parsed?.id ? parsed : null
  } catch {
    return null
  }
}

function write(attribution: PersistedAdAttribution): void {
  if (!hasWindow()) return
  Cookie.set(MONTILLA_AD_COOKIE_NAME, JSON.stringify(attribution), {
    secure: isSecureContext(),
    sameSite: 'Lax',
    domain: getCookieDomain(),
    expires: COOKIE_MAX_AGE_DAYS,
  })
}

export interface AdAttributionStore {
  /**
   * Resolve first-touch attribution against the current URL's `mad` param.
   * Persists the first valid id seen; returns the persisted parts (or null).
   * `nowIso` is injected so callers/tests control the timestamp.
   */
  resolve: (searchParams: URLSearchParams, paramName: string, nowIso: () => string) => (AdLinkParts & { id: string }) | null
  /** Read the persisted first-touch parts without touching the URL. */
  peek: () => (AdLinkParts & { id: string }) | null
  /** Flatten the persisted first touch into `montilla_ad_*` session attributes. */
  sessionAttrs: (searchParams: URLSearchParams, paramName: string, nowIso: () => string) => Record<string, string>
  /** Clear the persisted attribution (used by reset() and tests). */
  clear: () => void
}

function partsFrom(p: PersistedAdAttribution): (AdLinkParts & { id: string }) | null {
  const parts = parseAdLinkId(p.id)
  return parts ? { id: p.id, ...parts } : null
}

export function getAdAttributionStore(): AdAttributionStore {
  return {
    resolve(searchParams, paramName, nowIso) {
      const persisted = read()
      if (persisted) return partsFrom(persisted)

      // No first touch yet — capture the current URL's mad if it's valid.
      const raw = searchParams.get(paramName)
      const parts = parseAdLinkId(raw)
      if (raw && parts) {
        write({ id: raw, ts: nowIso() })
        return { id: raw, ...parts }
      }
      return null
    },
    peek() {
      const persisted = read()
      return persisted ? partsFrom(persisted) : null
    },
    sessionAttrs(searchParams, paramName, nowIso) {
      const resolved = this.resolve(searchParams, paramName, nowIso)
      return resolved ? adAttributionAttrs(resolved.id, resolved) : {}
    },
    clear() {
      if (!hasWindow()) return
      Cookie.remove(MONTILLA_AD_COOKIE_NAME, { domain: getCookieDomain() })
    },
  }
}
